import React, { useEffect, useState } from 'react';
import { Button, ButtonToolbar, Label } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import { Edit } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import validate from './Validate';
import renderField from '../../../shared/components/form/RenderField';
import { cpfMasker } from '../../../shared/components/form/Masks';
import Select from '../../../shared/components/form/Select';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import SmartAvatar from '../../../shared/components/form/SmartAvatar';
import SelectVirtualized from '../../../shared/components/form/SelectVirtualized';
import AddressInfo from '../../../shared/components/form/AddressInfo';

const EmployeeForm = ({
  t,
  handleBack,
  handleSubmit,
  dto,
  initialize,
  schoolTerm,
  toggleChangePass,
  change,
  accessProfile,
  schoolClass,
  subjects,
  communicationChannels,
  showLoading,
  hideLoading,
  showCommunicationChannel,
  onChangeProfile,
  selectedSchoolTerm,
  onChangeSchoolTerm,
}) => {
  const [profilePhotoUrl, setProfilePhotoUrl] = useState();

  const handleProfilePhotoChange = (file) => {
    if (file) {
      change('fileUrl', file.fileUrl);
      change('fileId', file.fileId);
      setProfilePhotoUrl(file.fileUrl);
    }
  };

  const handleUserChange = (event) => {
    if (
      (event.charCode >= 48 && event.charCode <= 57) ||
      (event.charCode >= 65 && event.charCode <= 90) ||
      (event.charCode >= 97 && event.charCode <= 122) ||
      event.charCode === 46
    ) {
      change(
        event.target.name,
        event.target.value.replace(/[^a-zA-Z0-9.]/gi, '')
      );
    } else {
      event.preventDefault();
    }
  };

  useEffect(() => {
    initialize(dto);
    handleProfilePhotoChange(dto);
  }, [dto]);

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div className="form__form-group">
        <div className="form__form-group--centered">
          <SmartAvatar
            name="fileUrl"
            avatarSrc={profilePhotoUrl}
            avatarChangeHandler={handleProfilePhotoChange}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.name')}</span>
        <div className="form__form-group-field">
          <Field
            name="name"
            type="text"
            component={renderField}
            maxLength="200"
            placeholder={t('placeholder.employee.name')}
          />
        </div>
      </div>
      <div className="form__form-group--3_columns">
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.user')}</span>
          <div className="form__form-group-field">
            <Field
              name="user"
              type="text"
              component={renderField}
              onKeyPress={handleUserChange}
              maxLength="100"
              placeholder={t('placeholder.employee.user')}
            />
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.password')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="password"
                type="password"
                component={renderField}
                maxLength="50"
                placeholder={t('placeholder.employee.password')}
                readOnly={!!dto.id}
              />
            </div>
          </div>
          {!!dto.id && (
            <div className="d-flex flex-grow-1 align-items-center">
              <Tooltip title={t('general.change-password')} arrow>
                <IconButton
                  className="icon-button"
                  aria-label="edit"
                  onClick={toggleChangePass}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.email')}</span>
        <div className="form__form-group-field">
          <Field
            name="email"
            type="email"
            component={renderField}
            maxLength="250"
            placeholder={t('general.email')}
          />
        </div>
      </div>
      <div className="form__form-group--2_columns">
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.cpf')}</span>
          <div className="form__form-group-field">
            <Field
              name="cpf"
              type="text"
              component={renderField}
              {...cpfMasker()}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.rg')}</span>
          <div className="form__form-group-field">
            <Field
              name="rg"
              type="text"
              component={renderField}
              maxLength="15"
              placeholder={t('placeholder.employee.rg')}
            />
          </div>
        </div>
      </div>
      <AddressInfo
        t={t}
        showLoading={showLoading}
        hideLoading={hideLoading}
        change={change}
        hideFields={{
          cellPhoneAux: true,
          state: true,
          city: true,
        }}
      />
      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.profiles')}</span>
        <div className="form__form-group-field">
          <Field
            name="profiles"
            component={Select}
            isMulti
            closeMenuOnSelect={false}
            options={accessProfile}
            placeholder={t('placeholder.employee.profiles')}
            onChange={onChangeProfile}
          />
        </div>
      </div>
      {showCommunicationChannel && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.communication-channels')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="channels"
              component={Select}
              isMulti
              closeMenuOnSelect={false}
              options={communicationChannels}
              placeholder={t('placeholder.employee.communication-channels')}
            />
          </div>
        </div>
      )}
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t('general.school-terms')}
        </span>
        <div className="form__form-group-field">
          <Field
            name="terms"
            component={Select}
            isMulti
            closeMenuOnSelect={false}
            options={schoolTerm}
            placeholder={t('placeholder.employee.school-terms')}
            onChange={onChangeSchoolTerm}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t('general.school-classes')}
        </span>
        <div className="form__form-group-field">
          <Field
            name="classes"
            component={Select}
            isMulti
            closeMenuOnSelect={false}
            options={schoolClass}
            isDisabled={!selectedSchoolTerm.length}
            placeholder={t('placeholder.employee.school-classes')}
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.subjects')}</span>
        <div className="form__form-group-field">
          <Field
            name="subjects"
            component={SelectVirtualized}
            isMulti
            closeMenuOnSelect={false}
            options={subjects}
            placeholder={t('placeholder.employee.subject')}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t('general.observations')}
        </span>
        <div className="form__form-group-field">
          <Field
            name="comment"
            type="text"
            component="textarea"
            maxLength="2000"
            placeholder={t('placeholder.employee.comments')}
          />
        </div>
      </div>
      <div className="form__form-group">
        <div className="form__form-group-field">
          <Label className="generic__label">{t('general.active')}</Label>
          <Field
            name="active"
            component={renderCheckBoxField}
            className="colored-click"
          />
        </div>
      </div>
      <ButtonToolbar className="form__button-toolbar">
        <Button color="primary" onClick={handleBack}>
          {t('general.back')}
        </Button>
        <Button color="success" type="submit">
          {t('general.save')}
        </Button>
      </ButtonToolbar>
    </form>
  );
};

EmployeeForm.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  toggleChangePass: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  schoolTerm: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  schoolClass: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  accessProfile: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    })
  ).isRequired,
  subjects: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  communicationChannels: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  selectedSchoolTerm: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  dto: PropTypes.shape(PropTypes.shape({})).isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  onChangeProfile: PropTypes.func.isRequired,
  onChangeSchoolTerm: PropTypes.func.isRequired,
  showCommunicationChannel: PropTypes.bool.isRequired,
};

export default reduxForm({
  form: 'EmployeeForm',
  validate,
})(withTranslation('common')(EmployeeForm));
